import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, GitBranch, Clock, CheckCircle2, RefreshCw, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
};
const fmtHours = (h) => {
    if (h == null) return '—';
    if (h < 1) return `${Math.round(h * 60)} min`;
    if (h < 24) return `${h.toFixed(1)} h`;
    return `${(h / 24).toFixed(1)} j`;
};

export default function OnboardingFunnelAdmin({ auth }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/super-admin/onboarding-funnel`, auth);
            setItems(r.data.items || []);
            setStats(r.data.stats || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué (schéma v24 appliqué ?)');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <div className="space-y-6" data-testid="sa-onboarding-funnel">
            {/* KPIs row */}
            {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Kpi icon={<GitBranch size={18} />} label="Espaces" value={stats.total_workspaces} tint="primary" testid="onb-kpi-total" />
                    <Kpi icon={<TrendingDown size={18} />} label="Démarré l'onboarding" value={`${stats.any_started_pct}%`} tint="amber" testid="onb-kpi-started" />
                    <Kpi icon={<CheckCircle2 size={18} />} label="Onboarding terminé" value={`${stats.completed_pct}%`} tint="emerald" sub={`${stats.completed_count} espaces`} testid="onb-kpi-completed" />
                    <Kpi icon={<Clock size={18} />} label="Durée moyenne (terminés)" value={fmtHours(stats.avg_completion_hours)} tint="zinc" testid="onb-kpi-duration" />
                </div>
            )}

            {/* Funnel visual */}
            {stats?.funnel_steps && (
                <div className="card-soft p-5" data-testid="onb-funnel-steps">
                    <h3 className="font-heading text-xl mb-1">Entonnoir étape par étape</h3>
                    <p className="text-sm text-foreground/65 mb-5">Pourcentage des espaces ayant validé chaque étape (taux d'abandon = différence)</p>
                    <div className="space-y-3">
                        {stats.funnel_steps.map((s, i) => {
                            const prev = i > 0 ? stats.funnel_steps[i - 1] : null;
                            const dropPct = prev ? Math.max(0, prev.pct - s.pct) : 0;
                            return (
                                <div key={s.key} className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-medium">{i + 1}. {s.label}</span>
                                        <span className="font-mono text-foreground/70">
                                            {s.count} <span className="text-foreground/50">·</span> {s.pct}%
                                            {dropPct > 0 && <span className="ml-2 text-rose-600 text-xs">↓ {dropPct.toFixed(1)}pt</span>}
                                        </span>
                                    </div>
                                    <div className="h-6 rounded bg-surface-muted overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-primary to-amber-400 transition-all duration-700 flex items-center justify-end pr-2 text-[10px] font-bold text-white"
                                            style={{ width: `${Math.max(2, s.pct)}%` }}>
                                            {s.pct >= 8 ? `${s.pct}%` : ''}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Workspace table */}
            <div className="card-soft p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-heading text-xl">Détail par workspace</h3>
                        <p className="text-sm text-foreground/65 mt-1">{items.length} espace(s)</p>
                    </div>
                    <Button variant="outline" size="icon" onClick={load} disabled={loading} data-testid="onb-refresh">
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    </Button>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-foreground/60"><Loader2 className="inline animate-spin mr-2" size={16} /> Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed rounded-lg text-foreground/55">Aucun espace pour l'instant.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-foreground/55 border-b">
                                    <th className="py-2 pr-3">Espace</th>
                                    <th className="py-2 pr-3">Secteur</th>
                                    <th className="py-2 pr-3">Créé le</th>
                                    <th className="py-2 pr-3">Progression</th>
                                    <th className="py-2 pr-3">Statut</th>
                                    <th className="py-2 pr-3">Durée</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((w) => {
                                    const pct = (w.steps_completed / w.steps_total) * 100;
                                    const done = !!w.onboarding_completed_at;
                                    return (
                                        <tr key={w.id} className="border-b last:border-0 hover:bg-surface-muted/40" data-testid={`onb-row-${w.id}`}>
                                            <td className="py-2.5 pr-3">
                                                <div className="font-medium">{w.name}</div>
                                                <div className="text-xs text-foreground/50">{w.country || '—'}</div>
                                            </td>
                                            <td className="py-2.5 pr-3 text-xs">{w.industry || '—'}</td>
                                            <td className="py-2.5 pr-3 text-xs text-foreground/60 whitespace-nowrap">{fmtDate(w.created_at)}</td>
                                            <td className="py-2.5 pr-3 min-w-[160px]">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                                                        <div className={`h-full transition-all ${done ? 'bg-emerald-500' : pct > 50 ? 'bg-amber-500' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="text-xs font-mono text-foreground/65 w-10 text-right">{w.steps_completed}/{w.steps_total}</span>
                                                </div>
                                            </td>
                                            <td className="py-2.5 pr-3">
                                                {done ? (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Terminé</span>
                                                ) : w.steps_completed === 0 ? (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-600">Pas démarré</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">En cours</span>
                                                )}
                                            </td>
                                            <td className="py-2.5 pr-3 text-xs">{fmtHours(w.duration_hours)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const Kpi = ({ icon, label, value, sub, tint, testid }) => {
    const tints = {
        primary: 'bg-primary-light text-primary ring-primary/15',
        emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
        amber: 'bg-amber-50 text-amber-700 ring-amber-100',
        zinc: 'bg-zinc-50 text-zinc-700 ring-zinc-200',
    };
    return (
        <div className={`card-soft p-4 flex items-start gap-3 ring-1 ${tints[tint] || tints.zinc}`} data-testid={testid}>
            <div className="h-9 w-9 rounded-md bg-white/60 flex items-center justify-center shrink-0">{icon}</div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
                <div className="font-heading text-xl mt-0.5 text-foreground">{value}</div>
                {sub && <div className="text-[11px] text-foreground/55 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
};
